package com.eladfinish.bible.contest.screens.verseWidget.ui

import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import coil3.compose.LocalPlatformContext
import coil3.compose.SubcomposeAsyncImage
import coil3.request.ImageRequest
import coil3.request.crossfade
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.close
import com.eladfinish.bible.contest.verse_widget_example_error
import com.eladfinish.bible.contest.verse_widget_example_image
import com.eladfinish.bible.contest.verse_widget_example_retry
import dev.icerock.moko.resources.compose.stringResource

/**
 * "What is a widget?": a picture of the widget on a home screen, loaded from [imageUrl], in a floating dialog.
 * It closes with the X, the close button, a tap outside it and Back. This is the one place that knows how the
 * explanation is shown, so replacing the dialog with a screen means replacing only this function.
 */
@Composable
fun VerseWidgetExample(imageUrl: String, onDismiss: () -> Unit) {
    Dialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(
            dismissOnBackPress = true,
            dismissOnClickOutside = true,
            // The picture is tall, so it gets the whole width and height of the window
            usePlatformDefaultWidth = false,
        ),
    ) {
        BoxWithConstraints(
            // The window covers the whole screen, so a tap beside the card is a tap inside the window:
            // the platform's "click outside" never fires, and it is done here
            modifier = Modifier
                .fillMaxSize()
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onDismiss,
                )
                .padding(horizontal = 16.dp, vertical = 24.dp),
            contentAlignment = Alignment.Center,
        ) {
            // What the header and the close button take; the picture fits in the rest
            val imageMaxHeight = (maxHeight - 112.dp).coerceAtLeast(160.dp)

            Surface(
                // A tap on the card itself must not reach the dismissing box behind it
                modifier = Modifier.pointerInput(Unit) { detectTapGestures { } },
                shape = MaterialTheme.shapes.large,
                color = MaterialTheme.colorScheme.surface,
                tonalElevation = 6.dp,
            ) {
                Column(
                    modifier = Modifier.padding(bottom = 8.dp),
                    horizontalAlignment = Alignment.CenterHorizontally,
                ) {
                    Row(modifier = Modifier.fillMaxWidth()) {
                        IconButton(onClick = onDismiss) {
                            Icon(
                                imageVector = Icons.Default.Close,
                                contentDescription = stringResource(SharedRes.strings.close),
                            )
                        }
                    }

                    ExampleImage(imageUrl = imageUrl, maxHeight = imageMaxHeight)

                    TextButton(onClick = onDismiss) {
                        Text(stringResource(SharedRes.strings.close))
                    }
                }
            }
        }
    }
}

@Composable
private fun ExampleImage(imageUrl: String, maxHeight: Dp) {
    // A new attempt builds a new request, so it asks the network again
    var attempt by remember { mutableIntStateOf(0) }

    key(attempt) {
        SubcomposeAsyncImage(
            model = ImageRequest.Builder(LocalPlatformContext.current)
                .data(imageUrl)
                .crossfade(enable = true)
                .build(),
            contentDescription = stringResource(SharedRes.strings.verse_widget_example_image),
            contentScale = ContentScale.Fit,
            modifier = Modifier
                .fillMaxWidth()
                .heightIn(max = maxHeight),
            loading = {
                CenteredBox {
                    CircularProgressIndicator()
                }
            },
            error = {
                CenteredBox {
                    Column(
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.spacedBy(12.dp),
                    ) {
                        Text(
                            text = stringResource(SharedRes.strings.verse_widget_example_error),
                            style = MaterialTheme.typography.bodyMedium,
                            textAlign = TextAlign.Center,
                        )
                        OutlinedButton(onClick = { attempt++ }) {
                            Text(stringResource(SharedRes.strings.verse_widget_example_retry))
                        }
                    }
                }
            },
        )
    }
}

@Composable
private fun CenteredBox(content: @Composable () -> Unit) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .heightIn(min = 240.dp)
            .padding(horizontal = 24.dp, vertical = 16.dp),
        contentAlignment = Alignment.Center,
    ) {
        content()
    }
}
